import type {
  ApiKeySummary,
  HealthInfo,
  IssuedApiKey,
  IssuedReadToken,
  LoginResult,
  MbtilesEntry,
  Permissions,
  ReadTokenSummary,
} from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  multipart?: FormData;
}

/**
 * 단일 admin 토큰을 메모리에 들고 모든 호출에 Bearer로 붙인다. localStorage 같은
 * 영속 보관은 XSS 노출 면을 키우므로 일부러 안 쓴다 — 새로고침 시 재로그인.
 */
class ApiClient {
  private token: string | null = null;

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    let body: BodyInit | undefined;
    if (opts.multipart) {
      body = opts.multipart;
    } else if (opts.body !== undefined) {
      body = JSON.stringify(opts.body);
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(path, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      signal: opts.signal,
    });

    if (res.status === 204) {
      return undefined as unknown as T;
    }

    const text = await res.text();
    let payload: unknown;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = text;
    }

    if (!res.ok) {
      const message =
        (payload && typeof payload === 'object' && 'message' in payload
          ? String((payload as { message?: unknown }).message ?? '')
          : '') || `HTTP ${res.status}`;
      throw new ApiError(res.status, payload, message);
    }
    return payload as T;
  }

  // ---- auth ----
  login(username: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    });
  }

  me(): Promise<{ username: string; role: 'admin' }> {
    return this.request('/api/auth/me');
  }

  logout(): Promise<void> {
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  // ---- health ----
  health(): Promise<HealthInfo> {
    return this.request('/api/health');
  }

  // ---- mbtiles ----
  listMbtiles(): Promise<MbtilesEntry[]> {
    return this.request('/api/mbtiles');
  }

  uploadMbtiles(input: {
    file: File;
    id?: string;
    displayName?: string;
    isPublic?: boolean;
  }): Promise<MbtilesEntry> {
    const fd = new FormData();
    fd.append('file', input.file);
    if (input.id) fd.append('id', input.id);
    if (input.displayName) fd.append('displayName', input.displayName);
    if (input.isPublic !== undefined) fd.append('public', String(input.isPublic));
    return this.request('/api/mbtiles', { method: 'POST', multipart: fd });
  }

  updateMbtilesMeta(
    id: string,
    patch: { public?: boolean; displayName?: string },
  ): Promise<MbtilesEntry> {
    return this.request(`/api/mbtiles/${encodeURIComponent(id)}/meta`, {
      method: 'PUT',
      body: patch,
    });
  }

  deleteMbtiles(id: string): Promise<void> {
    return this.request(`/api/mbtiles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  // ---- api keys ----
  listApiKeys(): Promise<ApiKeySummary[]> {
    return this.request('/api/keys');
  }

  issueApiKey(name: string, expiresAt?: string): Promise<IssuedApiKey> {
    return this.request('/api/keys', {
      method: 'POST',
      body: expiresAt ? { name, expiresAt } : { name },
    });
  }

  revokeApiKey(id: string): Promise<void> {
    return this.request(`/api/keys/${id}`, { method: 'DELETE' });
  }

  // ---- read tokens ----
  listReadTokens(): Promise<ReadTokenSummary[]> {
    return this.request('/api/read-tokens');
  }

  issueReadToken(input: {
    name: string;
    permissions: Permissions;
    expiresAt?: string;
  }): Promise<IssuedReadToken> {
    return this.request('/api/read-tokens', {
      method: 'POST',
      body: input,
    });
  }

  revokeReadToken(id: string): Promise<void> {
    return this.request(`/api/read-tokens/${id}`, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
